import { useCallback, useEffect, useMemo, useState } from 'react'
import Styles from './PropertyForm.module.scss'
import cx from 'classnames'
import { Field, FormikErrors, FormikTouched } from 'formik'
import PropertyIcon from 'common/PropertyItem/PropertyIcon'
import { startCase } from 'lodash'
import { PropertySearch } from '../PropertySearch/PropertySearch'
import NewPropertyActionItem from '../PropertyActions/NewPropertyActionItem'
import { useScrollableTable } from 'hooks/useScrollableTable'
import Foco from 'react-foco'
import TypeConfirmationModal from './TypeConfirmationModal'
import { LazyComponentWrapper } from 'components/LazyComponentWrapper/LazyComponentWrapper'
import { ReactComponent as Cheveron } from '../PropertyValue/icons/cheveron-down-small__16.svg'
import { PropertyType } from 'api/clients/PropertyClient'
import { type Values as FormValues } from './PropertyForm'
import { useDebounce } from '../../hooks/useDebounce'

interface Props {
  errors: FormikErrors<FormValues>
  touched: FormikTouched<FormValues>
  type: PropertyType
  propertyTypes: FormValues[]
  onSelectType: (index: number) => void
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
  isEdit: boolean
  disabled?: boolean
}

// if you change this, change it also in the css
export const SingleMultiSelectOptionsHeight = 36

const TypeSelect = ({ errors, touched, type, propertyTypes, onSelectType, isEdit, disabled = false }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [confrimationModalVisible, setConfrimationModalVisible] = useState(false)
  const [inputQuery, setInputQuery] = useState('')
  const [formPropertyTypes, setFormPropertytypes] = useState(propertyTypes)
  const debouncedInputQuery = useDebounce(inputQuery, 300)

  useEffect(() => {
    if (!debouncedInputQuery) {
      setFormPropertytypes(propertyTypes)
    }

    setFormPropertytypes(
      propertyTypes.filter((type) => type.name.toLowerCase().includes(debouncedInputQuery.toLowerCase())),
    )
  }, [debouncedInputQuery, propertyTypes])

  const onSelectIndex = useCallback(
    (index: number) => {
      setConfrimationModalVisible(false)
      onSelectType(propertyTypes.findIndex((value) => value.type === formPropertyTypes[index].type))
      setIsOpen(false)
    },
    [onSelectType, formPropertyTypes, propertyTypes],
  )

  const initialSelection = useMemo(() => {
    return formPropertyTypes.findIndex((pt) => pt?.name === type)
  }, [formPropertyTypes, type])

  const handleOnSelectType = useCallback(
    (index: number) => {
      if (isEdit && formPropertyTypes[index].type !== type) {
        setConfrimationModalVisible(true)
      } else {
        onSelectIndex(index)
      }

      setIsOpen(false)
    },
    [isEdit, onSelectIndex, formPropertyTypes, type],
  )

  const { selectedIndex, setSelectedIndex } = useScrollableTable({
    initialSelection,
    enabled: isOpen,
    rowsCount: formPropertyTypes.length - 1,
    onSelect: handleOnSelectType,
    onCancel: () => {},
  })

  const renderTypeConfirmationModal = useCallback(() => {
    if (!confrimationModalVisible) return <></>

    return (
      <LazyComponentWrapper
        component={
          <TypeConfirmationModal
            visible={confrimationModalVisible}
            onConvert={() => onSelectIndex(selectedIndex)}
            setVisible={(visible: boolean) => setConfrimationModalVisible(visible)}
          />
        }
      />
    )
  }, [confrimationModalVisible, onSelectIndex, setConfrimationModalVisible, selectedIndex])

  const renderTypesDropdown = useCallback(
    () => (
      <>
        <PropertySearch
          className={Styles.searchWrapper}
          placeholder={'Find a property type...'}
          query={inputQuery}
          iconVisible={true}
          onChange={setInputQuery}
          onCancel={() => {}}
        />
        <Foco onClickOutside={() => setIsOpen(false)}>
          {!!formPropertyTypes.length && (
            <div className={Styles.containerPrompt}>
              <div className={Styles.resultsList}>
                {formPropertyTypes.map((propertyType, index) => {
                  return (
                    <NewPropertyActionItem
                      key={`${propertyType.type}-${index}`}
                      type={propertyType.type}
                      showCheckIcon={true}
                      active={index === selectedIndex}
                      onMouseEnter={() => setSelectedIndex(index)}
                      onClick={() => handleOnSelectType(index)}
                    />
                  )
                })}
              </div>
            </div>
          )}
        </Foco>
      </>
    ),
    [handleOnSelectType, inputQuery, formPropertyTypes, selectedIndex, setSelectedIndex],
  )

  const renderTypeSelect = useCallback(
    () => (
      <div
        onClick={() => !disabled && setIsOpen(true)}
        className={cx(Styles.formInput, Styles.formSelect, {
          [Styles.inputError]: errors.type && touched.type,
          [Styles.disabledInput]: disabled,
        })}
      >
        <PropertyIcon type={type} />
        <Field disabled={disabled} type="text" name="type" placeholder="Type" value={startCase(type)} />
        <Cheveron className={Styles.cheveronIcon} />
      </div>
    ),
    [disabled, setIsOpen, type, errors.type, touched.type],
  )

  return (
    <div className={Styles.formSelectContainer}>
      {renderTypeConfirmationModal()}
      {isOpen ? renderTypesDropdown() : renderTypeSelect()}
    </div>
  )
}

export default TypeSelect
